import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight, Play } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { IMAGES } from "@/data/content";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { seoCopy } from "@/data/seoCopy";
import { Marquee } from "@/components/Marquee";
import { CtaBand } from "./Home";

const resolveImg = (key) => {
  const m = key.match(/^([a-z]+)(\d)$/);
  if (m && Array.isArray(IMAGES[m[1]])) return IMAGES[m[1]][Number(m[2])];
  return IMAGES[key];
};

const EASE = [0.16, 1, 0.3, 1];

export default function ServicePage({ slug }) {
  const { t, lang } = useLanguage();
  const page = t.servicePages[slug];
  const common = t.servicePages;

  return (
    <main data-testid={`service-page-${slug}`}>
      <Seo title={`RV FILM · ${t.nav[slug]}`} description={page.desc} />
      <section className="relative pt-20 md:pt-28 pb-16 px-6 lg:px-12 overflow-hidden">
        <div className="absolute -right-40 -top-20 w-[480px] h-[480px] spotlight-red" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto">
          <Reveal y={12}>
            <Link
              to="/"
              data-testid="service-back-home"
              className="inline-flex items-center gap-2 f-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 hover:text-slate-200 transition-colors mb-10"
            >
              <ArrowLeft size={13} />
              {common.backHome}
            </Link>
          </Reveal>
          <p className="f-mono text-[11px] uppercase tracking-[0.3em] text-red-500 mb-6" data-testid="service-kicker">
            {page.kicker}
          </p>
          <h1 className="max-w-5xl" key={lang} data-testid="service-title">
            {[page.titleA, page.titleB].map((line, i) => (
              <div key={i} className="overflow-hidden">
                <span
                  className={`block f-display font-black tracking-tight leading-[0.95] text-[12vw] sm:text-6xl lg:text-8xl ${
                    i === 1 ? "f-serif italic font-normal text-red-500" : "text-slate-50"
                  }`}
                >
                  {line}
                </span>
              </div>
            ))}
          </h1>
          <Reveal delay={0.2}>
            <p className="mt-8 text-slate-400 text-base sm:text-lg leading-relaxed max-w-2xl" data-testid="service-desc">
              {page.desc}
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-14 letterbox rounded-lg overflow-hidden border border-white/10 group">
              <img
                src={resolveImg(page.heroImg)}
                alt={page.kicker}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <Marquee />

      <section className="py-24 md:py-32 px-6 lg:px-12" data-testid="service-deliverables">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <h2 className="f-display text-3xl sm:text-4xl font-bold tracking-tight mb-14">{common.deliverablesTitle}</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 rounded-lg overflow-hidden">
            {page.deliverables.map((d, i) => (
              <Reveal key={i} delay={i * 0.07} className="bg-[#101114] p-8 md:p-10 group hover:bg-[#16181D] transition-colors duration-500">
                <div className="flex items-start justify-between gap-4">
                  <h3 className="f-display text-xl md:text-2xl font-bold tracking-tight" data-testid={`deliverable-title-${i}`}>
                    {d.title}
                  </h3>
                  <ArrowUpRight size={18} className="text-slate-600 group-hover:text-red-500 transition-colors shrink-0 mt-1" />
                </div>
                <p className="mt-4 text-sm text-slate-400 leading-relaxed">{d.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {seoCopy[lang][slug] && (
        <section className="pb-24 px-6 lg:px-12" data-testid="service-seo-text">
          <div className="max-w-3xl mx-auto">
            <Reveal>
              <p className="text-slate-400 leading-relaxed text-base md:text-lg">{seoCopy[lang][slug]}</p>
            </Reveal>
          </div>
        </section>
      )}

      {page.directors && (
        <section className="pb-24 md:pb-32 px-6 lg:px-12" data-testid="service-directors">
          <div className="max-w-7xl mx-auto">
            <Reveal>
              <p className="f-mono text-[11px] uppercase tracking-[0.3em] text-red-500 mb-6">Regia</p>
              <h2 className="f-display text-3xl sm:text-4xl font-bold tracking-tight mb-14">{page.directorsTitle}</h2>
            </Reveal>
            <div className="border-t border-white/10">
              {page.directors.map((d, i) => (
                <Reveal key={d.name} delay={i * 0.06}>
                  <div
                    className="flex items-baseline justify-between gap-6 py-6 border-b border-white/10 group"
                    data-testid={`director-${i}`}
                  >
                    <span className="f-display text-2xl md:text-4xl font-extrabold tracking-tight text-slate-200 group-hover:text-red-500 transition-colors duration-300">
                      {d.name}
                    </span>
                    {d.note && (
                      <span className="f-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">{d.note}</span>
                    )}
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {page.spots && (
        <section className="pb-24 md:pb-32 px-6 lg:px-12" data-testid="service-spots">
          <div className="max-w-7xl mx-auto">
            <Reveal>
              <h2 className="f-display text-3xl sm:text-4xl font-bold tracking-tight mb-14">{page.spotsTitle || common.spotsTitle}</h2>
            </Reveal>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {page.spots.map((s, i) => (
                <Reveal key={s.title} delay={i * 0.07}>
                  <div className="rounded-lg overflow-hidden border border-white/10 bg-[#101114] hover:border-red-600/40 transition-colors duration-500">
                    <div className="aspect-video bg-black">
                      {s.external ? (
                        <a
                          href={`https://youtu.be/${s.youtube}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="relative block w-full h-full group/ext"
                          data-testid={`spot-video-${i}`}
                        >
                          <img
                            src={`https://i.ytimg.com/vi/${s.youtube}/hqdefault.jpg`}
                            alt={s.title}
                            className="w-full h-full object-cover opacity-80 transition-all duration-500 group-hover/ext:opacity-100 group-hover/ext:scale-105"
                          />
                          <span className="absolute inset-0 flex items-center justify-center">
                            <span className="w-14 h-14 rounded-full bg-red-600 flex items-center justify-center transition-transform duration-300 group-hover/ext:scale-110">
                              <Play size={18} fill="white" className="text-white ml-0.5" />
                            </span>
                          </span>
                        </a>
                      ) : s.youtube ? (
                        <iframe
                          src={`https://www.youtube.com/embed/${s.youtube}?rel=0`}
                          title={s.title}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          data-testid={`spot-video-${i}`}
                        />
                      ) : (
                        <video
                          src={s.url}
                          controls
                          preload="metadata"
                          playsInline
                          className="w-full h-full"
                          data-testid={`spot-video-${i}`}
                        />
                      )}
                    </div>
                    <div className="p-5 flex items-center justify-between gap-4">
                      <h3 className="f-display text-lg md:text-xl font-bold tracking-tight" data-testid={`spot-title-${i}`}>
                        {s.title}
                      </h3>
                      <span className="f-mono text-[10px] uppercase tracking-[0.2em] text-red-500 whitespace-nowrap">
                        {s.meta}
                      </span>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="pb-24 md:pb-32 px-6 lg:px-12" data-testid="service-gallery">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <h2 className="f-display text-3xl sm:text-4xl font-bold tracking-tight mb-14">{common.galleryTitle}</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {page.gallery.map((g, i) => {
              const isVideo = typeof g === "object";
              return (
                <Reveal key={isVideo ? g.title : g} delay={i * 0.08} className={isVideo ? "md:col-span-2" : ""}>
                  <div className={`rounded-lg overflow-hidden border border-white/10 group ${i === 1 && !isVideo ? "md:mt-10" : ""}`}>
                    {isVideo ? (
                      <div className="aspect-video bg-black">
                        <video
                          src={g.video}
                          controls
                          preload="metadata"
                          playsInline
                          className="w-full h-full"
                          data-testid={`gallery-video-${i}`}
                        />
                      </div>
                    ) : (
                      <div className="aspect-[4/5] overflow-hidden">
                        <img
                          src={resolveImg(g)}
                          alt={`${page.kicker} — ${i + 1}`}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                          data-testid={`gallery-img-${i}`}
                        />
                      </div>
                    )}
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-16 flex flex-col md:flex-row md:items-center justify-between gap-8 border-t border-white/10 pt-12">
              <div>
                <h3 className="f-display text-2xl md:text-3xl font-bold tracking-tight">{common.ctaTitle}</h3>
                <p className="mt-3 text-slate-400 text-sm md:text-base max-w-lg">{common.ctaDesc}</p>
              </div>
              <Link
                to="/contatti"
                data-testid="service-cta-button"
                className="group inline-flex items-center gap-3 bg-red-600 hover:bg-red-500 transition-colors duration-300 text-white f-mono text-xs uppercase tracking-[0.2em] px-8 py-4 rounded-full shrink-0"
              >
                {common.ctaButton}
                <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <CtaBand />
    </main>
  );
}
